import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";
import { ChevronDown, ChevronRight, Loader2, Trash2 } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";

import PageHeader from "../../components/PageHeader";
import { SELECT_STYLES } from "../../components/DateFilters/constants";
import { BrazilDate } from "../../utils/BrazilDate";
import { lojaService } from "../../services/lojaService";
import {
  fetchLimpezaDiariaLogs,
  LIMPEZA_DIARIA_CATEGORIA_LABELS,
} from "../../services/limpezaDiariaService";

type LojaOpt = { value: number; label: string };
type CategoriaOpt = { value: string; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;
const CATEGORIA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  CategoriaOpt,
  false
>;

function getDefaultMonthRange() {
  const now = new Date();
  const tz = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: tz });
  const monthStr = format(now, "MM", { timeZone: tz });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

const DEFAULT_MONTH = getDefaultMonthRange();

const categoriaOptions: CategoriaOpt[] = Object.entries(
  LIMPEZA_DIARIA_CATEGORIA_LABELS
).map(([value, label]) => ({ value, label }));

function formatDateTimeBr(iso: string): string {
  return BrazilDate.formatBR(new Date(iso), true);
}

function formatRefDia(iso: string): string {
  return BrazilDate.formatBR(BrazilDate.fromISOString(iso), false);
}

const LimpezaPendentes: React.FC = () => {
  const [startDate, setStartDate] = useState(DEFAULT_MONTH.dataInicio);
  const [endDate, setEndDate] = useState(DEFAULT_MONTH.dataFim);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [categoria, setCategoria] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [applied, setApplied] = useState<null | {
    dataInicio: string;
    dataFim: string;
    lojaId: number | null;
    categoria: string | null;
  }>(null);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;
  const categoriaSel =
    categoriaOptions.find((o) => o.value === categoria) ?? null;

  const { data, isFetching } = useQuery({
    queryKey: ["limpeza-diaria-logs", applied, page],
    enabled: applied != null,
    queryFn: () =>
      fetchLimpezaDiariaLogs({
        dataInicio: applied!.dataInicio,
        dataFim: applied!.dataFim,
        lojaId: applied!.lojaId,
        categoria: applied!.categoria ?? undefined,
        page,
        limit: 50,
      }),
  });

  const handleAplicar = () => {
    setPage(1);
    setExpandedId(null);
    setApplied({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId,
      categoria,
    });
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Pendentes excluídos na limpeza diária"
        description="Itens que ficaram pendentes de um dia para o outro e foram removidos automaticamente às 03:00."
      />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="limpeza-ini"
              className="text-xs font-medium text-neutral-700"
            >
              Referência — início
            </label>
            <input
              id="limpeza-ini"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="limpeza-fim"
              className="text-xs font-medium text-neutral-700"
            >
              Referência — fim
            </label>
            <input
              id="limpeza-fim"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">Loja</span>
            <ReactSelect<LojaOpt, false>
              value={lojaSel}
              onChange={(o: SingleValue<LojaOpt>) =>
                setLojaId(o?.value ?? null)
              }
              options={lojaOptions}
              isClearable
              placeholder="Todas"
              styles={LOJA_SELECT_STYLES}
              className="text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">
              Categoria
            </span>
            <ReactSelect<CategoriaOpt, false>
              value={categoriaSel}
              onChange={(o: SingleValue<CategoriaOpt>) =>
                setCategoria(o?.value ?? null)
              }
              options={categoriaOptions}
              isClearable
              placeholder="Todas"
              styles={CATEGORIA_SELECT_STYLES}
              className="text-sm"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      {applied == null ? (
        <p className="text-sm text-neutral-600">
          Selecione o período de referência e clique em{" "}
          <strong>Aplicar filtros</strong>.
        </p>
      ) : (
        <div className="relative">
          {isFetching && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          <div className="mb-4 flex items-center gap-2 text-sm text-neutral-600">
            <Trash2 size={16} className="text-neutral-500" aria-hidden />
            <span>
              {data?.total ?? 0} registro(s) entre{" "}
              <strong>{formatRefDia(applied.dataInicio)}</strong> e{" "}
              <strong>{formatRefDia(applied.dataFim)}</strong>
            </span>
          </div>

          {(data?.itens ?? []).length === 0 ? (
            <div className="rounded-lg border border-neutral-200 bg-white px-4 py-10 text-center text-sm text-neutral-500 shadow-sm">
              Nenhum item excluído no período.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white shadow-sm">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-neutral-200 bg-neutral-50">
                  <tr>
                    <th className="w-8 px-2 py-3" />
                    <th className="px-3 py-3 font-semibold text-neutral-800">
                      Ref. dia
                    </th>
                    <th className="px-3 py-3 font-semibold text-neutral-800">
                      Excluído em
                    </th>
                    <th className="px-3 py-3 font-semibold text-neutral-800">
                      Categoria
                    </th>
                    <th className="px-3 py-3 font-semibold text-neutral-800">
                      Loja
                    </th>
                    <th className="px-3 py-3 font-semibold text-neutral-800">
                      Resumo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.itens ?? []).map((item) => {
                    const open = expandedId === item.id;
                    return (
                      <React.Fragment key={item.id}>
                        <tr className="border-b border-neutral-100">
                          <td className="px-2 py-2">
                            <button
                              type="button"
                              className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
                              onClick={() =>
                                setExpandedId(open ? null : item.id)
                              }
                              aria-expanded={open}
                              aria-label="Ver detalhes"
                            >
                              {open ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-neutral-800">
                            {formatRefDia(item.referenciaDia)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-neutral-700">
                            {formatDateTimeBr(item.execucaoEm)}
                          </td>
                          <td className="px-3 py-2.5 text-neutral-800">
                            {LIMPEZA_DIARIA_CATEGORIA_LABELS[item.categoria] ??
                              item.categoria}
                          </td>
                          <td className="px-3 py-2.5 text-neutral-700">
                            {item.lojaNome ?? "—"}
                          </td>
                          <td className="px-3 py-2.5 text-neutral-900">
                            {item.resumo}
                          </td>
                        </tr>
                        {open ? (
                          <tr className="border-b border-neutral-100 bg-neutral-50/80">
                            <td colSpan={6} className="px-4 py-3">
                              <p className="mb-1 text-xs font-medium text-neutral-600">
                                ID original: {item.entidadeOrigemId}
                              </p>
                              <pre className="max-h-64 overflow-auto rounded-md border border-neutral-200 bg-white p-3 text-xs text-neutral-800">
                                {JSON.stringify(item.payload, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {(data?.totalPages ?? 0) > 1 ? (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-sm text-neutral-600">
                Página {page} de {data?.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= (data?.totalPages ?? 1)}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          ) : null}
        </div>
      )}
    </main>
  );
};

export default LimpezaPendentes;
